import { tzOffset } from '@date-fns/tz';
import {
    IntervalEntry,
    IntervalEntryConfig,
    IntervalEntryKind,
    IntervalFieldMode,
    IntervalOption,
    IntervalReport,
    IntervalReportDay,
} from 'api/report/types';
import { getIntervalTimestampParts } from './timestamp';

const millisecondsPerDay = 24 * 60 * 60 * 1000;

export interface IntervalEntryDraft extends Omit<
    IntervalEntry,
    'breakMinutes' | 'distanceKm'
> {
    reportingDate: string;
    startDate: string;
    startTime: string;
    endDate: string;
    endTime: string;
    breakMinutes: string;
    distanceKm: string;
}

export type IntervalEntryErrors = Partial<
    Record<keyof IntervalEntryDraft | 'timeRange', string>
>;

export interface IntervalEntryValidation {
    errors: IntervalEntryErrors;
    // Valid pairs involving a repeated clock time; ordinary times have no choices.
    timeOptions: IntervalTimeOption[];
}

export type IntervalEntryValidations = Record<string, IntervalEntryValidation>;

export interface IntervalDayDraft extends Omit<IntervalReportDay, 'entries'> {
    entries: IntervalEntryDraft[];
}

export interface IntervalReportDraft extends Omit<IntervalReport, 'days'> {
    days: IntervalDayDraft[];
}

/**
 * Treat clock fields as UTC only to get a timezone-independent numeric reference.
 * "2026-10-25", "02:30" becomes 02:30Z here; its actual Stockholm instant is
 * determined separately. The ISO round-trip rejects values Date.parse normalizes,
 * e.g. February 30 becoming March 2, as well as malformed/incomplete input.
 */
function parseWallTimeAsUTC(date: string, time: string): number {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !/^\d{2}:\d{2}$/.test(time)) {
        return NaN;
    }
    const timestamp = Date.parse(`${date}T${time}:00Z`);
    return Number.isFinite(timestamp) &&
        new Date(timestamp).toISOString().slice(0, 16) === `${date}T${time}`
        ? timestamp
        : NaN;
}

/**
 * Find the instants matching a date and clock time in the report timezone.
 * Try nearby UTC offsets and keep candidates that convert back to the input.
 * Returns one normally, two for a repeated time, or none for skipped/invalid input.
 * Each result preserves the input date/time and adds its valid offset.
 */
function getIntervalTimestampOptions(
    date: string,
    time: string,
    timeZone: string
): string[] {
    const wallTimeAsUTC = parseWallTimeAsUTC(date, time);
    if (!Number.isFinite(wallTimeAsUTC)) {
        return [];
    }

    // Probe HOURS around the numeric reference, including the previous/next day,
    // to collect offsets on both sides of a nearby clock change. These are probe
    // positions, not assumed clock-change sizes; Intl supplies the actual offsets.
    // Example: Stockholm, input 2026-10-25 02:30 (reference = 02:30Z):
    // hours   sample (UTC)       local date/time       offset (minutes)
    //   -36   Oct 23 14:30Z      Oct 23 16:30           120
    //   -12   Oct 24 14:30Z      Oct 24 16:30           120
    //     0   Oct 25 02:30Z      Oct 25 03:30            60
    //    12   Oct 25 14:30Z      Oct 25 15:30            60
    //    36   Oct 26 14:30Z      Oct 26 15:30            60
    // tzOffset returns each offset in minutes. Set leaves {120, 60}.
    const offsets = new Set(
        [-36, -12, 0, 12, 36].map(hours => {
            const sample = new Date(wallTimeAsUTC + hours * 3_600_000);
            return tzOffset(timeZone, sample);
        })
    );
    return (
        [...offsets]
            // A larger offset gives an earlier instant: +02:00 before +01:00.
            .sort((a, b) => b - a)
            .map(offset =>
                getIntervalTimestampParts(
                    wallTimeAsUTC - offset * 60_000,
                    timeZone
                )
            )
            .filter(candidate => {
                // Try each offset, then check that Intl converts the candidate back
                // to the input. For Oct 25 02:30 in Stockholm:
                // 120 => 00:30Z => 02:30 local; 60 => 01:30Z => 02:30 local.
                // Both survive. A skipped spring time has no matching candidate.
                return candidate.date === date && candidate.time === time;
            })
            // The example becomes 02:30:00+02:00 and 02:30:00+01:00 on Oct 25.
            .map(candidate => candidate.timestamp)
    );
}

export interface IntervalTimeOption {
    startAt: string;
    endAt: string;
    elapsedMinutes: number;
    clockChange: 'before' | 'across' | 'after';
}

type IntervalTimeFields = Pick<
    IntervalEntryDraft,
    'reportingDate' | 'startDate' | 'startTime' | 'endDate' | 'endTime'
>;

/** Keep each actual interval distinct, even when two choices have equal duration. */
function getTimeOptionsFromCandidates(
    draft: IntervalTimeFields,
    starts: string[],
    ends: string[],
    timeZone: string
): { options: IntervalTimeOption[]; ambiguous: boolean } {
    const ambiguous = starts.length > 1 || ends.length > 1;
    const options: IntervalTimeOption[] = [];
    // Unique endpoints are used directly; validation still checks their date/range.
    if (!ambiguous) {
        return { options, ambiguous };
    }
    // Compare calendar dates using UTC references: the next date is +24h even
    // when the actual reporting day lasts 23 or 25 hours in the report timezone.
    const reportingDay = parseWallTimeAsUTC(draft.reportingDate, '00:00');
    const datesInRange = [draft.startDate, draft.endDate].every(date => {
        const day = parseWallTimeAsUTC(date, '00:00');
        return (
            day === reportingDay || day === reportingDay + millisecondsPerDay
        );
    });
    if (!datesInRange) {
        return { options, ambiguous };
    }

    // Pair every start occurrence with every end occurrence. For Stockholm on
    // 2026-10-25, 02:10 -> 02:40 gives:
    // start offset / end offset   elapsed   classification
    // +02:00 / +02:00              30 min   before
    // +02:00 / +01:00              90 min   across
    // +01:00 / +02:00             -30 min   discarded
    // +01:00 / +01:00              30 min   after (a distinct actual interval)
    starts.forEach((startAt, startIndex) => {
        ends.forEach((endAt, endIndex) => {
            const elapsedMinutes =
                (Date.parse(endAt) - Date.parse(startAt)) / 60_000;
            // Use elapsed time, before subtracting breaks. Oct 24 03:29 ->
            // Oct 25 02:30 in Stockholm gives 23h 1m or 24h 1m: discard the latter.
            // Starting at 03:30 instead gives 23h or exactly 24h, so both survive.
            if (elapsedMinutes <= 0 || elapsedMinutes > 24 * 60) {
                return;
            }
            let clockChange: IntervalTimeOption['clockChange'];
            // Different numeric offsets identify a pair spanning the clock change,
            // including a change to UTC, which ISO timestamps represent with Z.
            if (
                tzOffset(timeZone, new Date(startAt)) !==
                tzOffset(timeZone, new Date(endAt))
            ) {
                clockChange = 'across';
            } else {
                // Candidates are ordered by instant: index 0 is the first
                // occurrence (before rollback), index 1 the second (after).
                // Use the endpoint that repeats; a unique endpoint's index is 0
                // regardless of which side of the clock change it falls on.
                const occurrence = starts.length > 1 ? startIndex : endIndex;
                clockChange = occurrence === 0 ? 'before' : 'after';
            }
            options.push({ startAt, endAt, elapsedMinutes, clockChange });
        });
    });
    return { options, ambiguous };
}

function getIntervalTimestamps(
    fields: IntervalTimeFields,
    timeZone: string
): Pick<IntervalEntryDraft, 'startAt' | 'endAt'> {
    const starts = getIntervalTimestampOptions(
        fields.startDate,
        fields.startTime,
        timeZone
    );
    const ends = getIntervalTimestampOptions(
        fields.endDate,
        fields.endTime,
        timeZone
    );
    const { options } = getTimeOptionsFromCandidates(
        fields,
        starts,
        ends,
        timeZone
    );
    // Filtering may leave only one possible pair even if a clock time repeats.
    const interval = options.length === 1 ? options[0] : null;
    return {
        startAt: interval?.startAt ?? (starts.length === 1 ? starts[0] : ''),
        endAt: interval?.endAt ?? (ends.length === 1 ? ends[0] : ''),
    };
}

function createIntervalEntryDraft(
    entry: IntervalEntry,
    timeZone: string,
    reportingDate: string
): IntervalEntryDraft {
    // The backend already chose the instants. The shared formatter only normalizes
    // their representation so clock-option selection and edit/revert compare equally.
    const start = getIntervalTimestampParts(
        Date.parse(entry.startAt),
        timeZone
    );
    const end = getIntervalTimestampParts(Date.parse(entry.endAt), timeZone);
    return {
        ...entry,
        reportingDate,
        startDate: start.date,
        startTime: start.time,
        startAt: start.timestamp,
        endDate: end.date,
        endTime: end.time,
        endAt: end.timestamp,
        breakMinutes: String(entry.breakMinutes),
        distanceKm: entry.distanceKm === null ? '' : String(entry.distanceKm),
    };
}

export function createEmptyIntervalEntryDraft(
    uuid: string,
    kind: IntervalEntryKind,
    reportingDate: string
): IntervalEntryDraft {
    return {
        uuid,
        kind,
        reportingDate,
        startDate: reportingDate,
        startTime: '',
        startAt: '',
        endDate: reportingDate,
        endTime: '',
        endAt: '',
        breakMinutes: '0',
        isSick: false,
        location: null,
        reason: null,
        distanceKm: '',
        note: '',
    };
}

export function isEmptyIntervalEntryDraft(draft: IntervalEntryDraft): boolean {
    return (
        draft.startDate === draft.reportingDate &&
        draft.endDate === draft.reportingDate &&
        draft.startTime === '' &&
        draft.endTime === '' &&
        draft.startAt === '' &&
        draft.endAt === '' &&
        (draft.breakMinutes === '' || draft.breakMinutes === '0') &&
        !draft.isSick &&
        draft.location === null &&
        draft.reason === null &&
        draft.distanceKm === '' &&
        draft.note === ''
    );
}

/** Sort only when opening a report so rows stay in place while their times change. */
export function createIntervalReportDraft(
    data: IntervalReport
): IntervalReportDraft {
    const cloned = structuredClone(data);
    return {
        ...cloned,
        days: cloned.days.map(day => ({
            ...day,
            entries: day.entries
                .sort((a, b) => Date.parse(a.startAt) - Date.parse(b.startAt))
                .map(entry =>
                    createIntervalEntryDraft(entry, data.timeZone, day.date)
                ),
        })),
    };
}

/** Compare editable values; option labels and holiday metadata are server-owned. */
export function serializeIntervalDraftValues(
    draft: IntervalReportDraft
): string {
    return JSON.stringify({
        notes: draft.notes,
        days: draft.days.map(day => ({
            date: day.date,
            entries: day.entries.map(entry => ({
                uuid: entry.uuid,
                kind: entry.kind,
                startDate: entry.startDate,
                startTime: entry.startTime,
                endDate: entry.endDate,
                endTime: entry.endTime,
                startAt: entry.startAt,
                endAt: entry.endAt,
                breakMinutes: entry.breakMinutes,
                isSick: entry.isSick,
                locationUUID: entry.location?.uuid ?? null,
                reasonUUID: entry.reason?.uuid ?? null,
                distanceKm: entry.distanceKm,
                note: entry.note,
            })),
        })),
    });
}

export function validateIntervalReportDraft(
    draft: IntervalReportDraft,
    entryKinds: IntervalEntryConfig[]
): IntervalEntryValidations {
    const validations: IntervalEntryValidations = {};
    for (const day of draft.days) {
        for (const entry of day.entries) {
            validations[entry.uuid] = validateIntervalEntry(
                entry,
                entryKinds.find(config => config.kind === entry.kind),
                draft.timeZone
            );
        }
    }
    return validations;
}

export function changeIntervalEntryTime(
    draft: IntervalEntryDraft,
    field: 'startDate' | 'startTime' | 'endDate' | 'endTime',
    value: string,
    timeZone: string
): IntervalEntryDraft {
    if (draft[field] === value) {
        return draft;
    }
    const timeFields: IntervalTimeFields = {
        reportingDate: draft.reportingDate,
        startDate: draft.startDate,
        startTime: draft.startTime,
        endDate: draft.endDate,
        endTime: draft.endTime,
        [field]: value,
    };
    return {
        ...draft,
        [field]: value,
        ...getIntervalTimestamps(timeFields, timeZone),
    };
}

export function changeIntervalEntryKind(
    draft: IntervalEntryDraft,
    config: IntervalEntryConfig
): IntervalEntryDraft {
    if (draft.kind === config.kind) return draft;
    return {
        ...draft,
        kind: config.kind,
        breakMinutes: config.allowBreaks ? draft.breakMinutes : '0',
        isSick: config.allowSickness && draft.isSick,
        location:
            config.location.mode !== IntervalFieldMode.DISABLED &&
            config.location.options.some(
                option => option.uuid === draft.location?.uuid
            )
                ? draft.location
                : null,
        reason:
            config.reason.mode !== IntervalFieldMode.DISABLED &&
            config.reason.options.some(
                option => option.uuid === draft.reason?.uuid
            )
                ? draft.reason
                : null,
        distanceKm:
            config.distanceMode === IntervalFieldMode.DISABLED
                ? ''
                : draft.distanceKm,
    };
}

function validateTimestamp(
    date: string,
    time: string,
    timestamp: string,
    options: string[]
): string | undefined {
    if (!Number.isFinite(parseWallTimeAsUTC(date, time))) {
        return 'Ange ett giltigt datum och klockslag.';
    }
    if (options.length === 0) {
        return 'Klockslaget finns inte eftersom klockan ställs fram.';
    }
    if (
        (timestamp || options.length === 1) &&
        !options.some(option => Date.parse(option) === Date.parse(timestamp))
    ) {
        return 'Ange ett giltigt datum och klockslag.';
    }
    return undefined;
}

function validateOption(
    value: IntervalOption | null,
    mode: IntervalFieldMode,
    options: IntervalOption[],
    label: string
): string | undefined {
    if (mode === IntervalFieldMode.DISABLED) {
        return undefined;
    }
    if (!value) {
        return mode === IntervalFieldMode.REQUIRED
            ? `Välj ${label.toLowerCase()}.`
            : undefined;
    }
    return options.some(option => option.uuid === value.uuid)
        ? undefined
        : 'Valet finns inte längre. Välj ett tillgängligt alternativ.';
}

function validateIntervalEntry(
    draft: IntervalEntryDraft,
    config: IntervalEntryConfig | undefined,
    timeZone: string
): IntervalEntryValidation {
    const errors: IntervalEntryErrors = {};
    const startOptions = getIntervalTimestampOptions(
        draft.startDate,
        draft.startTime,
        timeZone
    );
    const endOptions = getIntervalTimestampOptions(
        draft.endDate,
        draft.endTime,
        timeZone
    );
    const { options: timeOptions, ambiguous } = getTimeOptionsFromCandidates(
        draft,
        startOptions,
        endOptions,
        timeZone
    );
    const startError = validateTimestamp(
        draft.startDate,
        draft.startTime,
        draft.startAt,
        startOptions
    );
    const endError = validateTimestamp(
        draft.endDate,
        draft.endTime,
        draft.endAt,
        endOptions
    );
    if (startError) errors.startTime = startError;
    if (endError) errors.endTime = endError;
    if (
        ambiguous &&
        timeOptions.length > 0 &&
        !timeOptions.some(
            option =>
                Date.parse(option.startAt) === Date.parse(draft.startAt) &&
                Date.parse(option.endAt) === Date.parse(draft.endAt)
        )
    ) {
        errors.timeRange = 'Välj vilket tidsintervall som avses.';
    }

    // These UTC midnights represent calendar dates, so adding one day stays exact
    // across clock changes. The actual elapsed-time limit is checked separately below.
    const reportingDay = parseWallTimeAsUTC(draft.reportingDate, '00:00');
    const startDay = parseWallTimeAsUTC(draft.startDate, '00:00');
    if (
        startDay !== reportingDay &&
        startDay !== reportingDay + millisecondsPerDay
    ) {
        errors.startDate =
            'Startdatum måste vara den valda dagen eller dagen efter.';
    }
    const endDay = parseWallTimeAsUTC(draft.endDate, '00:00');
    if (
        endDay !== reportingDay &&
        endDay !== reportingDay + millisecondsPerDay
    ) {
        errors.endDate =
            'Slutdatum måste vara den valda dagen eller dagen efter.';
    }
    // Unselected repeated times still need useful range errors when no pair is valid.
    const elapsed = errors.timeRange
        ? NaN
        : (Date.parse(draft.endAt || endOptions[0]) -
              Date.parse(draft.startAt || startOptions[0])) /
          60_000;
    if (elapsed <= 0) {
        errors.endTime = 'Sluttiden måste vara efter starttiden.';
    } else if (elapsed > 24 * 60) {
        errors.endTime = 'En rad får omfatta högst 24 timmar.';
    }

    const breakText = draft.breakMinutes.trim();
    const breakMinutes = breakText === '' ? 0 : Number(breakText);
    if (!/^\d*$/.test(breakText) || !Number.isSafeInteger(breakMinutes)) {
        errors.breakMinutes = 'Ange ett helt antal minuter, minst 0.';
    } else if (elapsed > 0 && breakMinutes >= elapsed) {
        errors.breakMinutes = 'Rasten måste vara kortare än tidsintervallet.';
    }

    const distanceText = draft.distanceKm.trim();
    const distanceKm = distanceText === '' ? null : Number(distanceText);
    if (
        distanceKm !== null &&
        (!/^\d+$/.test(distanceText) || !Number.isSafeInteger(distanceKm))
    ) {
        errors.distanceKm = 'Ange ett helt antal kilometer, minst 0.';
    }

    if (!config || config.kind !== draft.kind) {
        errors.kind = 'Välj en tillgänglig typ av tid.';
    } else {
        if (
            distanceKm === null &&
            config.distanceMode === IntervalFieldMode.REQUIRED
        ) {
            errors.distanceKm = 'Ange körsträcka.';
        }
        const locationError = validateOption(
            draft.location,
            config.location.mode,
            config.location.options,
            config.location.label || 'Plats'
        );
        const reasonError = validateOption(
            draft.reason,
            config.reason.mode,
            config.reason.options,
            config.reason.label || 'Orsak'
        );
        if (locationError) errors.location = locationError;
        if (reasonError) errors.reason = reasonError;
    }

    return { errors, timeOptions };
}
